import { type UIEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Combobox, Input, InputBase, Text, useCombobox } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import classes from './ParishCombobox.module.sass';
import {
  useChurchAdminCurrentScopesInfinite,
  useDotMenuScopeVisibility,
  useGlobalSettings,
  useLastSelectedChurch,
} from '@/hooks';
import type { CurrentScopeListedItem, ScopeListedItem } from '@/types';
import { ROUTE_ALIASES } from '@/routing/routesAliases';

type ParishComboboxProps = {
  className?: string;
  onChurchChange?: () => void;
};

export function ParishCombobox({ className, onChurchChange }: ParishComboboxProps) {
  const { t } = useTranslation('global');
  const navigate = useNavigate();
  const location = useLocation();
  const { currentChurch } = useGlobalSettings();
  const { data: currentScope, fetchNextPage, hasNextPage, isFetchingNextPage } = useChurchAdminCurrentScopesInfinite();
  const { setLastSelectedChurch } = useLastSelectedChurch();
  const isScopeVisibleInDotMenu = useDotMenuScopeVisibility();
  const optionsContainerRef = useRef<HTMLDivElement>(null);
  const pendingChurchRef = useRef<ScopeListedItem | null>(null);

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      combobox.focusTarget();
    },

    onDropdownOpen: (eventSource) => {
      if (eventSource === 'keyboard') {
        combobox.selectActiveOption();
      } else {
        combobox.updateSelectedOptionIndex('active');
      }
    },
  });

  const [value, setValue] = useState<ScopeListedItem | null>(currentChurch);

  useEffect(() => {
    setValue(currentChurch);
  }, [currentChurch]);

  useEffect(() => {
    if (pendingChurchRef.current && location.pathname === ROUTE_ALIASES.HOME) {
      const church = pendingChurchRef.current;
      pendingChurchRef.current = null;
      setLastSelectedChurch(church);
    }
  }, [location.pathname, setLastSelectedChurch]);

  const getScopeLabel = (scope: ScopeListedItem | null | undefined) => {
    if (!scope) return '';
    if (scope.zipCode) return `${scope.churchName} - ${scope.zipCode}`;
    return scope.churchName;
  };

  const handleChurchChange = (churchId: string) => {
    const selectedChurch = filteredScopes.find((scope) => scope?.churchId === churchId);

    if (selectedChurch) {
      const isChurchChanged = selectedChurch.churchId !== value?.churchId;

      if (isChurchChanged) {
        setValue(selectedChurch);

        if (location.pathname === ROUTE_ALIASES.HOME) {
          setLastSelectedChurch(selectedChurch);
        } else {
          pendingChurchRef.current = selectedChurch;
          navigate(ROUTE_ALIASES.HOME, { replace: true });
        }

        showNotification({
          title: t('notifications.successTitle'),
          message: t('notifications.church.changeSuccessMessage', { churchName: selectedChurch.churchName }),
          color: 'success',
          position: 'top-center',
        });

        combobox.updateSelectedOptionIndex('active');
        combobox.closeDropdown();
        onChurchChange?.();
        return;
      }

      setValue(selectedChurch);
      setLastSelectedChurch(selectedChurch);
    }

    combobox.updateSelectedOptionIndex('active');
    combobox.closeDropdown();
    onChurchChange?.();
  };

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 20; // 20px threshold

      if (isNearBottom && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  const filteredScopes =
    currentScope?.scopes.filter((item) => {
      if (!isScopeVisibleInDotMenu) {
        return true;
      }

      return isScopeVisibleInDotMenu(item.churchId);
    }) ?? [];

  const options =
    filteredScopes.map((item: CurrentScopeListedItem) => (
      <Combobox.Option value={item?.churchId} key={item?.churchId} className={classes.option}>
        {getScopeLabel(item)}
      </Combobox.Option>
    )) || [];

  return (
    <Combobox
      store={combobox}
      withinPortal={false}
      onOptionSubmit={handleChurchChange}
      classNames={{ dropdown: className }}
    >
      <Combobox.Target>
        <InputBase
          component="button"
          description={t('mainHeader.userMenu.currentParish')}
          type="button"
          pointer
          rightSection={<Combobox.Chevron />}
          onClick={() => combobox.toggleDropdown()}
          rightSectionPointerEvents="none"
          classNames={{ input: classes.input, root: classes.root }}
        >
          {value ? (
            <Text pr="lg" size="lg">
              {getScopeLabel(value)}
            </Text>
          ) : (
            <Input.Placeholder>{t('mainHeader.userMenu.selectParish')}</Input.Placeholder>
          )}
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options ref={optionsContainerRef} mah={400} style={{ overflowY: 'auto' }} onScroll={handleScroll}>
          {options.length > 0 ? options : <Combobox.Empty>{t('mainHeader.userMenu.noParishesFound')}</Combobox.Empty>}
          {isFetchingNextPage && (
            <Combobox.Option value="loading" disabled>
              {t('mainHeader.userMenu.loadingMoreParishes')}
            </Combobox.Option>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
